import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, Clock, Save, CheckCircle2 } from "lucide-react";
import { InfoCard } from "@/components/toggles";

// Sub-Batch Q-1b: Global Processing-Delay Settings (Studio)
export default function StudioProcessingDelayPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [mpDays, setMpDays] = useState(0);
  const [stxDays, setStxDays] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user || user === false) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/seller/settings");
        setMpDays(data.processing_delay_mp_days || 0);
        setStxDays(data.processing_delay_stx_days || 0);
        setUpdatedAt(data.updated_at);
      } catch (e) {
        setError(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, navigate]);

  const save = async () => {
    setError(""); setSaving(true); setSaved(false);
    try {
      const { data } = await api.patch("/seller/settings", {
        processing_delay_mp_days: Math.max(0, Math.min(60, parseInt(mpDays, 10) || 0)),
        processing_delay_stx_days: Math.max(0, Math.min(60, parseInt(stxDays, 10) || 0)),
      });
      setMpDays(data.processing_delay_mp_days);
      setStxDays(data.processing_delay_stx_days);
      setUpdatedAt(data.updated_at);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="studio-processing-delay-page" className="pb-16">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
        <button data-testid="delay-back" onClick={() => navigate("/studio")} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-[#0A0A0A]" />
          <h1 className="text-base font-bold text-[#0A0A0A]">{t("studio_delay.page_title")}</h1>
        </div>
      </div>

      <div className="px-4 py-5 space-y-5">
        <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5]">
          <p className="text-xs text-[#666] leading-relaxed">
            Lege hier eine <strong>{t("studio_delay.general_delay")}</strong> für deine Versand-Bearbeitung pro Bereich fest – z.&nbsp;B.
            weil du nur an Wochenenden versendest, im Urlaub bist oder grundsätzlich länger zur Vorbereitung brauchst.
            Diese Tage werden in der Lieferzeiten-Queue der Plattform zu deiner Standard-SLA hinzugerechnet,
            zusätzlich zu artikelspezifischen Verzögerungen aus dem Listing-Editor (Schritt 6).
          </p>
        </div>

        {error && (
          <InfoCard variant="danger">
            <p className="text-xs text-red-700">{error}</p>
          </InfoCard>
        )}

        <div className="border border-[#E5E5E5] divide-y divide-[#E5E5E5]">
          <div className="p-4">
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Marketplace · Standard</label>
            <p className="text-[11px] text-[#999] mt-0.5">{t("studio_delay.sla5_hint")}</p>
            <input
              data-testid="delay-mp-days"
              type="number"
              min="0"
              max="60"
              step="1"
              value={mpDays}
              onChange={(e) => setMpDays(e.target.value)}
              className="mt-2 w-32 h-11 px-3 border border-[#E5E5E5] text-sm text-center font-mono focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            />
            <span className="ml-2 text-xs text-[#666]">{t("studio_delay.extra_days")}</span>
          </div>
          <div className="p-4">
            <label className="text-xs font-medium text-[#666] tracking-wider">stilltouchedX · Diskret</label>
            <p className="text-[11px] text-[#999] mt-0.5">{t("studio_delay.sla7_hint")}</p>
            <input
              data-testid="delay-stx-days"
              type="number"
              min="0"
              max="60"
              step="1"
              value={stxDays}
              onChange={(e) => setStxDays(e.target.value)}
              className="mt-2 w-32 h-11 px-3 border border-[#E5E5E5] text-sm text-center font-mono focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            />
            <span className="ml-2 text-xs text-[#666]">{t("studio_delay.extra_days")}</span>
          </div>
        </div>

        {updatedAt && (
          <p className="text-[11px] text-[#999]">Zuletzt aktualisiert: {new Date(updatedAt).toLocaleString("de-DE")}</p>
        )}

        <button
          data-testid="delay-save"
          onClick={save}
          disabled={saving}
          className="w-full rounded-full h-12 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#222] disabled:opacity-50"
        >
          {saved ? <><CheckCircle2 size={16} /> Gespeichert</> : saving ? "Speichere..." : <><Save size={14} /> {t("studio_delay.save_changes")}</>}
        </button>
      </div>
    </div>
  );
}
